import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MarketplaceSearch } from '../components/marketplace/MarketplaceSearch';
import { ProductFilter } from '../components/marketplace/ProductFilter';
import { ProductCard } from '../components/marketplace/ProductCard';
import { useTranslation } from '../context/I18nContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiStar, FiArrowRight, FiShield } = FiIcons;

const MOCK_PRODUCTS = [
  {
    id: 'p1',
    name: 'San Marzano Tomaten DOP',
    supplier_name: 'Gusto d\'Italia',
    supplier_image: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400',
    description: 'De authentieke vulkanische tomaten uit Campanië.',
    category: 'produce',
    category_label: 'Veld & Boomgaard',
    origin_country: 'Italië',
    origin_region: 'Campanië',
    price: 4.80,
    unit: 'kg',
    rating: 4.9,
    status_text: 'Morgen geleverd',
    image_url: 'https://images.unsplash.com/photo-1592924357228-91a4daadcfea?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p2',
    name: 'Wilde Cantharellen',
    supplier_name: 'Bosch & Duin',
    supplier_image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?auto=format&fit=crop&q=80&w=400',
    description: 'Wild geplukte goudgele cantharellen met een peperige smaak.',
    category: 'produce',
    category_label: 'Veld & Boomgaard',
    origin_country: 'Nederland',
    origin_region: 'Veluwe',
    price: 24.50,
    unit: 'kg',
    rating: 4.8,
    status_text: 'Seizoensproduct',
    image_url: 'https://images.unsplash.com/photo-1570710891163-6d3b5c47248b?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p3',
    name: 'Zeeuwse Oesters Creuses',
    supplier_name: 'Noordzee Catch',
    supplier_image: 'https://images.unsplash.com/photo-1534080564583-6be75777b70a?auto=format&fit=crop&q=80&w=400',
    description: 'Premium oesters direct uit de Oosterschelde.',
    category: 'sea',
    category_label: 'Kustvangst',
    origin_country: 'Nederland',
    origin_region: 'Zeeland',
    price: 18.00,
    unit: 'doos (12st)',
    rating: 5.0,
    status_text: 'Vandaag leverbaar',
    image_url: 'https://images.unsplash.com/photo-1599458252204-763ff81b1f42?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p4',
    name: 'Boeren Brie de Meaux',
    supplier_name: 'L\'Artisan',
    supplier_image: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400',
    description: 'Rauwemelkse brie met een rijke, complexe smaak.',
    category: 'dairy',
    category_label: 'Zuivel & Kaas',
    origin_country: 'Frankrijk',
    origin_region: 'Seine-et-Marne',
    price: 32.00,
    unit: 'wiel',
    rating: 4.7,
    status_text: 'Vandaag leverbaar',
    image_url: 'https://images.unsplash.com/photo-1452195100486-9cc805987862?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p5',
    name: 'Burrata di Puglia',
    supplier_name: 'Terra Madre',
    supplier_image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?auto=format&fit=crop&q=80&w=400',
    description: 'Ultra-verse burrata met een hart van stracciatella.',
    category: 'dairy',
    category_label: 'Zuivel & Kaas',
    origin_country: 'Italië',
    origin_region: 'Puglia',
    price: 3.50,
    unit: 'stuk',
    rating: 4.9,
    status_text: 'Morgen geleverd',
    image_url: 'https://images.unsplash.com/photo-1626958660920-305364817a01?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p6',
    name: 'Extra Vergine Olijfolie',
    supplier_name: 'Olivares de Jaén',
    supplier_image: 'https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400',
    description: 'Koudgeperste Picual olijfolie, oogst 2024.',
    category: 'wine',
    category_label: 'Wijngaard & Kelder',
    origin_country: 'Spanje',
    origin_region: 'Andalusië',
    price: 18.90,
    unit: 'fles (750ml)',
    rating: 4.8,
    status_text: 'Vandaag leverbaar',
    image_url: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?auto=format&fit=crop&q=80&w=800'
  },
  {
    id: 'p7',
    name: 'Perzische Saffraan Negin',
    supplier_name: 'Silk Road Spices',
    supplier_image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?auto=format&fit=crop&q=80&w=400',
    description: 'Hoogste kwaliteit saffraan draden voor sublieme aroma.',
    category: 'produce',
    category_label: 'Veld & Boomgaard',
    origin_country: 'Iran',
    origin_region: 'Khorasan',
    price: 12.50,
    unit: 'gram',
    rating: 5.0,
    status_text: 'Morgen geleverd',
    image_url: 'https://images.unsplash.com/photo-1600122292728-c11608c02888?auto=format&fit=crop&q=80&w=800'
  }
];

const StatItem = ({ label, value }) => (
  <div className="text-center px-10 border-r border-zinc-100 last:border-0 grow">
    <p className="text-3xl font-black text-zinc-900 mb-2 tracking-tighter">{value}</p>
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400">{label}</p>
  </div>
);

const SupplierCard = ({ name, location, image, rating, description }) => (
  <motion.div 
    whileHover={{ y: -10 }}
    className="bg-white rounded-[2.5rem] p-8 border border-zinc-100 shadow-sm hover:shadow-2xl hover:shadow-zinc-200/60 transition-all duration-500 group"
  >
    <div className="flex items-start gap-8 mb-8">
      <div className="w-24 h-24 rounded-[1.5rem] overflow-hidden flex-shrink-0 ring-4 ring-zinc-50 shadow-inner">
        <img src={image} alt={name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-center gap-3 mb-2">
          <h4 className="text-2xl font-black text-zinc-900 truncate tracking-tight">{name}</h4>
          <SafeIcon icon={FiShield} className="w-5 h-5 text-[#D4B483]" />
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs font-bold text-zinc-400 flex items-center gap-1.5 uppercase tracking-wider">
            <SafeIcon icon={FiMapPin} className="w-3 h-3 text-[#D4B483]" /> {location}
          </p>
          <span className="w-1 h-1 bg-zinc-200 rounded-full" />
          <p className="text-xs font-black text-[#1A2E22] flex items-center gap-1">
            <SafeIcon icon={FiStar} className="w-3 h-3 text-amber-400 fill-amber-400" /> {rating}
          </p>
        </div>
      </div>
    </div>
    
    <p className="text-sm text-zinc-500 leading-relaxed mb-8 line-clamp-2 font-medium">
      {description || "Toonaangevende producent van hoogwaardige ingrediënten, direct geleverd aan de beste keukens van de regio."}
    </p>

    <button className="w-full py-4 bg-zinc-50 group-hover:bg-zinc-900 group-hover:text-white text-zinc-900 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 flex items-center justify-center gap-2">
      Bekijk leverancier <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
    </button>
  </motion.div>
);

export const Marketplace = () => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [activeCategory, searchQuery]);

  const filteredProducts = MOCK_PRODUCTS.filter(p => {
    const matchesCategory = activeCategory === 'all' || p.category === activeCategory;
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          p.supplier_name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#FCFAF8]">
      {/* Optimized Hero Layout */}
      <section className="relative h-[80vh] min-h-[700px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&q=80&w=2000" 
            alt="Marketplace Hero" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-black/35 backdrop-blur-[0.5px]" />
        </div>
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            {/* Generous badge spacing */}
            <span className="inline-block px-7 py-2.5 rounded-full bg-white/5 backdrop-blur-2xl border border-white/10 text-white text-[10px] font-black uppercase tracking-[0.6em] mb-24">
              {t('marketplace.tag')}
            </span>

            {/* Premium Editorial Typography: weight 700, -10% size, 0.02em tracking, 1.12 line-height */}
            <h1 className="text-5xl md:text-[6.5rem] font-bold text-white mb-20 tracking-[0.02em] leading-[1.12] max-w-6xl mx-auto drop-shadow-2xl">
              {t('marketplace.title')}
            </h1>

            {/* Airy Subtitle */}
            <p className="text-xl md:text-2xl text-white/80 mb-20 max-w-3xl mx-auto font-light leading-relaxed tracking-wide">
              {t('marketplace.subtitle')}
            </p>

            {/* Search Bar - Positioned to overlap by ~60px */}
            <div className="w-full max-w-2xl transform translate-y-[60px]">
              <MarketplaceSearch 
                searchQuery={searchQuery} 
                setSearchQuery={setSearchQuery} 
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Statistics Section - Appears underneath Search Bar */}
      <div className="relative z-20 pt-24 container mx-auto px-6">
        <div className="bg-white rounded-[4.5rem] shadow-2xl shadow-zinc-200/50 border border-zinc-100/50 p-14 flex flex-wrap items-center justify-center gap-y-12">
          <StatItem value="250+" label="Producenten" />
          <StatItem value="12" label="Provincies" />
          <StatItem value="3.500+" label="Ingrediënten" />
          <StatItem value="4,9★" label="Gemiddelde beoordeling" />
        </div>
      </div>

      <div className="container mx-auto px-6 py-40">
        {/* Categories Section - Enlarged by 15% */}
        <section className="mb-48">
          <header className="mb-20 text-center md:text-left">
            <h2 className="text-5xl font-black text-zinc-900 tracking-tighter mb-6">{t('home.categories.title')}</h2>
            <div className="h-1.5 w-20 bg-[#D4B483] rounded-full mx-auto md:mx-0" />
          </header>
          <ProductFilter 
            activeCategory={activeCategory} 
            setActiveCategory={setActiveCategory} 
          />
        </section>

        {/* Featured Suppliers Section */}
        <section className="mb-48">
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-20">
            <div className="text-center md:text-left">
              <h2 className="text-5xl font-black text-zinc-900 tracking-tighter mb-6">Uitgelichte leveranciers</h2>
              <div className="h-1.5 w-20 bg-[#D4B483] rounded-full mx-auto md:mx-0" />
            </div>
            <button className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400 hover:text-zinc-900 transition-all flex items-center justify-center gap-2">
              Bekijk alles <SafeIcon icon={FiArrowRight} />
            </button>
          </header>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <SupplierCard 
              name="Van der Meer Farm" 
              location="Utrecht, NL" 
              image="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?auto=format&fit=crop&q=80&w=400" 
              rating="4.9"
              description="Specialist in biologische vollegrondsgroenten en vergeten rassen uit het hart van de regio Utrecht."
            />
            <SupplierCard 
              name="Noordzee Catch" 
              location="Zeeland, NL" 
              image="https://images.unsplash.com/photo-1534080564583-6be75777b70a?auto=format&fit=crop&q=80&w=400" 
              rating="5.0"
              description="Dagverse aanvoer van duurzaam gevangen vis en schaaldieren rechtstreeks uit de Zeeuwse wateren."
            />
          </div>
        </section>

        {/* Results Section */}
        <section>
          <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-24">
            <div className="text-center md:text-left">
              <h2 className="text-5xl font-black text-zinc-900 tracking-tighter mb-6">Marktplaats resultaten</h2>
              <div className="h-1.5 w-20 bg-[#D4B483] rounded-full mx-auto md:mx-0" />
            </div>
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-[0.3em] bg-white px-6 py-3 rounded-full shadow-sm border border-zinc-50 self-center">
              {filteredProducts.length} Producten gevonden
            </p>
          </header>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-72 gap-8">
              <div className="w-24 h-24 border-4 border-zinc-100 border-t-[#D4B483] rounded-full animate-spin" />
              <p className="text-zinc-400 font-black uppercase tracking-[0.4em] text-[10px]">{t('marketplace.loadingDesc')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-12 gap-y-28">
              <AnimatePresence mode="popLayout">
                {filteredProducts.map((product, idx) => (
                  <ProductCard 
                    key={product.id} 
                    product={product} 
                    delay={idx * 0.05} 
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};